/**
 * Модель для работы с настройками прав и ролей
 */

import { createModel } from '@rematch/core';
import { RootModel } from './index';

import { IId, IRoleType, IRole } from './types';

import UsersService from '../services/UsersService';
import adminService from '../services/AdminService';
import { parseSecurableTypes } from '../utils/parseSecurableTypes';
import { buildRightsAclPayload, buildRoleAclPayload } from '../utils/secTreeAcl';

interface IData {
  roles: IRoleType[];
  nodeACL: any;
}

interface IList {
  data: IRole[] | null;
  pagesCount: number;
}

interface IState {
  data: IData;
  list: IList;
  listRoles: IRole[] | null;
  secTreeHierarchy: Record<string, unknown>;
  roleItem: IRole | null;
}

const initialState: IState = {
  data: {
    roles: [],
    nodeACL: {}
  },
  list: {
    data: null,
    pagesCount: 0
  },
  listRoles: null,
  secTreeHierarchy: {},
  roleItem: null
};

export const settingSecTree = createModel<RootModel>()({
  state: initialState,
  reducers: {
    setData(state: IState, payload: IData): IState {
      return { ...state, data: { ...state.data, ...payload } };
    },
    setListRoles(state: IState, payload: IRole[]): IState {
      return { ...state, listRoles: payload };
    },
    // setList(state: IState, payload: IList): IState {
    //   return { ...state, list: payload };
    // },
    setHierarchy(state: IState, payload: any): IState {
      return { ...state, secTreeHierarchy: payload };
    },
    setRoleItem(state: IState, payload: IRole): IState {
      return { ...state, roleItem: payload };
    },
    reset(): IState {
      return { ...initialState };
    }
  },
  effects: () => ({
    async getData() {
      try {
        const [{ data: roles }] = await Promise.all([UsersService.getRoles()]);

        this.setData({ roles });
      } catch (error) {
        return error;
      }
    },
    async getListRoles() {
      try {
        const { data } = await UsersService.getRoles();

        this.setListRoles(data);
      } catch (error) {
        return error;
      }
    },
    // async getList(params, rootState: iRootState) {
    //   try {
    //     const { data } = await ProtectionsService.getTypeAll(params);
    //
    //     this.setList(data);
    //   } catch (error) {
    //     return error;
    //   }
    // },
    async getHierarchy(params: any, rootState?: any) {
      try {
        const { data } = await UsersService.getSecTreeHierarchy();

        this.setHierarchy(data);
      } catch (error) {
        return error;
      }
    },
    async getNodeACL(props: any) {
      try {
        const { data } = await UsersService.getNodeACL(props.root, props.roleId);
        const obj: any = {};
        data.forEach((item: { uid: IId; roleId: IId; action: IId; result: string }) => {
          if (!obj[item.uid]) {
            obj[item.uid] = {};
          }
          if (props.roleId) {
            obj[item.uid][item.action] = item.result === 'Grant';
          } else {
            if (!obj[item.uid][item.roleId + 'roleId']) {
              obj[item.uid][item.roleId + 'roleId'] = {};
            }
            obj[item.uid][item.roleId + 'roleId'][item.action] = item.result === 'Grant';
          }
        });

        this.setData({
          nodeACL: obj
        });
      } catch (error) {
        return error;
      }
    },
    /**
     * Fetches and parses securable type codes for a tree node.
     * Does not update Redux — Rights/Roles pages store the result locally (see LoadedSecurableTypes)
     * so fast tree navigation cannot render stale columns from a shared store.
     */
    async getSecurableTypes(id: IId) {
      try {
        const { data } = await adminService.getSecurableTypes(id);

        return { securableTypes: parseSecurableTypes(data) };
      } catch (error) {
        return error;
      }
    },
    async getRoleItem(id: IId) {
      try {
        const { data } = await UsersService.getRole(id);

        this.setRoleItem(data);
      } catch (error) {
        return error;
      }
    },

    async updateOrCreate(body: { id?: IId; name?: string; friendlyName?: string; actions: Record<string, Record<string, boolean>> }) {
      try {
        const roleBody = {
          id: (body.id ?? 0) as IId,
          name: (body.name ?? body.friendlyName ?? '').toString(),
          friendlyName: (body.friendlyName ?? body.name ?? '').toString()
        };

        const { data } = await UsersService.updateOrCreateRole(roleBody);
        await UsersService.updateOrCreateACL(buildRoleAclPayload(body.actions, data.id));
      } catch (error) {
        return error;
      }
    },

    async updateOrCreateACL(body: { actions: Record<string, Record<string, Record<string, boolean>>> }) {
      try {
        await UsersService.updateOrCreateACL(buildRightsAclPayload(body.actions));
      } catch (error) {
        return error;
      }
    },

    async remove(id: IId) {
      try {
        await UsersService.deleteRole(id);
      } catch (error) {
        return error;
      }
    }
  })
});
